/**
 * bd-personalized-picks.js
 *
 * Swaps the product carousel rendered by sections/bd-personalized-picks.liquid
 * for the collection that matches the visitor's browse affinity.
 *
 * Every page on this store is served from Shopify's full-page cache, so the
 * server cannot know who is asking. The section is therefore rendered with its
 * fallback collection for everybody, and this element replaces the contents
 * client-side using the Section Rendering API:
 *
 *   GET /collections/<handle>?section_id=<section id>
 *
 * Requesting the section under a collection URL renders it with `collection`
 * bound to that collection, which is how the section resolves its products,
 * heading and "view all" link — the same technique the "Recently viewed"
 * section uses against the search URL.
 *
 * Two forms of the request are tried, because the Section Rendering API only
 * documents a 404 for an ID that "doesn't exist on the theme":
 *   1. the section instance ID — carries this instance's settings, so the
 *      matched interest's heading override comes through;
 *   2. the section file name — always resolvable, but rendered with default
 *      settings, which the section handles by treating the requested
 *      collection as the target and using its title as the heading.
 * The first form that returns usable markup wins.
 *
 * Nothing swaps for a first-time visitor (no affinity yet), in the theme
 * editor, or when the visitor's interest already matches what was rendered —
 * so the extra request only happens when it changes what is on screen.
 */
if (!customElements.get('bd-personalized-picks')) {
  class BDPersonalizedPicks extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      if (window.Shopify && window.Shopify.designMode) return;

      this.fetchAttempts = 0;

      if (window.BDAffinity && window.BDAffinity.ready) {
        this.schedule();
      } else {
        document.addEventListener('on:bd-affinity:ready', this.schedule.bind(this), { once: true });
      }
    }

    schedule() {
      if (window.initLazyScript) {
        window.initLazyScript(this, this.personalize.bind(this), 800);
      } else {
        this.personalize();
      }
    }

    get interests() {
      const source = this.querySelector('[data-bd-interests]');
      if (!source) return [];

      try {
        return JSON.parse(source.textContent) || [];
      } catch (error) {
        return [];
      }
    }

    /** Section IDs to try, most specific first. */
    sectionIds() {
      const ids = [this.dataset.sectionId];
      if (this.dataset.sectionType && this.dataset.sectionType !== this.dataset.sectionId) {
        ids.push(this.dataset.sectionType);
      }
      return ids.filter(Boolean);
    }

    /** Renders the section for `url`, or null if this ID doesn't resolve there. */
    async render(url, sectionId) {
      const response = await fetch(`${url}?section_id=${encodeURIComponent(sectionId)}`);
      if (!response.ok) return null;

      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();

      const rendered = tmpl.content.querySelector('bd-personalized-picks');
      // No cards means the section resolved but had nothing to show — e.g.
      // default settings with no collection in context.
      return rendered && rendered.querySelector('product-card, .card--product') ? rendered : null;
    }

    async personalize() {
      const affinity = window.BDAffinity;
      if (!affinity) return;

      const minScore = parseFloat(this.dataset.minScore);
      const pick = affinity.match(this.interests, isNaN(minScore) ? undefined : minScore);

      // No interest strong enough, or the fallback is already the right one.
      if (!pick || !pick.url || pick.handle === this.dataset.current) return;

      try {
        let replacement = null;

        for (const sectionId of this.sectionIds()) {
          replacement = await this.render(pick.url, sectionId);
          if (replacement) break;
        }

        if (!replacement) return;

        this.innerHTML = replacement.innerHTML;
        this.dataset.current = replacement.dataset.current || pick.handle;
        this.setAttribute('data-personalized', 'true');

        if (window.initLazyImages) window.initLazyImages();
      } catch (error) {
        this.fetchAttempts += 1;
        // Two retries, then keep the fallback collection that is already shown.
        if (this.fetchAttempts < 3) this.personalize();
      }
    }
  }

  customElements.define('bd-personalized-picks', BDPersonalizedPicks);
}
